import React from "react";

import {
  BearerGroup,
  CapabilityPartDetail,
  CapabilityPartRelationGroup,
} from "../../types/capabilityPartDetail";
import type { EntityDetailMode } from "../../types/entityRegistry";
import type { DetailSection } from "../Detail/DetailSections";
import GroupedRelationList, {
  RelationGroup,
} from "../Detail/GroupedRelationList";
import SourceTable from "../Detail/SourceTable";
import { codeLabel, codeWords } from "../Detail/format";
import PartBearers, { countBearers } from "./PartBearers";
import PartClaimedWith, { claimedWithGroups } from "./PartClaimedWith";
import PartOverview from "./PartOverview";
import PartShowsUp, {
  CLAIMED_TOGETHER,
  SHOWS_UP,
  hasShowsUp,
} from "./PartShowsUp";

// What the sections need beside the part
export interface PartSectionsOptions {
  mode: EntityDetailMode;
  // True while the bearers shown are those of the page asked for before
  turning: boolean;
  // Asks for another page of one bearer group
  onBearerPage: (group: BearerGroup, offset: number) => void;
}

// A count for the strip, where the section holds anything to count
const countOf = (count: number): number | undefined =>
  count > 0 ? count : undefined;

// The basis of a relation a document states as such; any other is named
const STATED = "stated";

// How a group of related parts is named. Outgoing, the relation is said of
// the page's part ("Narrower than"); incoming, it is said of the other part
// towards this one ("Narrower than this").
const relationLabel = (group: CapabilityPartRelationGroup): string =>
  group.direction === "outgoing"
    ? codeLabel(group.relation)
    : `${codeLabel(group.relation)} this`;

// The place of a part among the parts: the parts filed under it, then the
// relations to other parts the tree does not draw. The broader part and the
// parts beside it are in the overview.
export const placeGroups = (part: CapabilityPartDetail): RelationGroup[] => [
  {
    label: "Narrower",
    items: (part.tree?.narrower ?? []).map((narrower) => ({
      entity: narrower,
      sources: [],
    })),
  },
  ...part.related.map((group) => ({
    label: relationLabel(group),
    items: group.items.map((relation) => ({
      entity: relation.part,
      note:
        [
          relation.summary,
          relation.basis && relation.basis !== STATED
            ? codeWords(relation.basis)
            : null,
        ]
          .filter(Boolean)
          .join(" · ") || null,
      sources: relation.sources,
    })),
  })),
];

/**
 * The sections of the part view, in the order shown. The same list serves
 * every role: a section the part has nothing in has no content, and so is
 * left out of the view.
 */
export const partSections = (
  part: CapabilityPartDetail,
  { mode, turning, onBearerPage }: PartSectionsOptions,
): DetailSection[] => {
  const bearers = countBearers(part.bearers);
  const place = placeGroups(part);
  const placed = place.reduce((count, group) => count + group.items.length, 0);
  // The claims of products, which "Claimed with" qualifies
  const claims = part.bearers.products.total;
  // The two lists of the section after the place: where the products show
  // up and the parts claimed together with this one. With one list alone
  // the section is named and counted by it.
  const showsUp = part.product_categories.items.length > 0;
  const together = part.claimed_together.items.length > 0;

  return [
    {
      id: "overview",
      label: "Overview",
      content: <PartOverview part={part} />,
    },
    {
      id: "claimed-with",
      label: "Claimed with",
      heading: `Claimed with · how the ${claims} ${claims === 1 ? "claim qualifies" : "claims qualify"} it`,
      content: claimedWithGroups(part).length > 0 && (
        <PartClaimedWith part={part} />
      ),
    },
    {
      id: "bearers",
      label: "Bearers",
      count: countOf(bearers),
      content: bearers > 0 && (
        <PartBearers
          // Another part starts at its first tab
          key={part.id}
          partId={part.id}
          bearers={part.bearers}
          turning={turning}
          onPage={onBearerPage}
        />
      ),
    },
    {
      id: "place",
      label: "Place among parts",
      count: countOf(placed),
      content: placed > 0 && <GroupedRelationList groups={place} />,
    },
    {
      // Each list heads its own column
      id: "shows-up",
      label: showsUp ? SHOWS_UP : CLAIMED_TOGETHER,
      hideHeading: true,
      count:
        showsUp && together
          ? undefined
          : countOf(
              showsUp
                ? part.product_categories.total
                : part.claimed_together.total,
            ),
      content: hasShowsUp(part) && <PartShowsUp part={part} />,
    },
    {
      id: "sources",
      label: "Sources",
      count: countOf(part.source_count),
      content: part.sources.items.length > 0 && (
        <SourceTable
          sources={part.sources.items}
          total={part.sources.total}
          mode={mode}
        />
      ),
    },
  ];
};
